import React from 'react';
import { Card, Space, DatePicker, Input, Select, message, Switch, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { SearchOutlined } from '@ant-design/icons';
import Decimal from 'decimal.js';

import StandardTable from '../../../components/common/StandardTable';
import { fetchSqlDataWithQuery } from '../../../services/api';
import { useGetMatClntWarehouses, useGetMatClntWhInventory } from '../../../hooks/genericHooks';
import MatClntMasterMaterialSelect from '../../../components/modals/MatClntMasterMaterialSelect';
import { formatNumberSmart } from '../../../utils/numberFormat';
import Button from '../../../components/common/Button';

const { RangePicker } = DatePicker;

interface LedgerFilters {
  dateRange?: [string, string] | null;
  itemcode?: string | null;
  warehouseId?: number | null;
}

type RawTxn = {
  id: number;
  txn_date: string;
  op_type: string;
  reference_no?: string | null;
  from_warehouse_id?: number | null;
  to_warehouse_id?: number | null;
  itemcode: string;
  qty: number;
  unit?: string | null;
  issued_to?: string | null;
  created_by?: string | null;
  notes?: string | null;
};

type LedgerRow = RawTxn & {
  in_qty: string;
  out_qty: string;
  balance: string;
};

const DEC_PLACES = 5;

const toDec = (v: unknown) => {
  if (v === null || v === undefined || v === '') return new Decimal(0);
  try {
    return new Decimal(v as any);
  } catch {
    return new Decimal(0);
  }
};

export default function MatClntMatLedger() {
  const [filters, setFilters] = React.useState<LedgerFilters>({
    dateRange: [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')],
    itemcode: null,
    warehouseId: null,
  });
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<LedgerRow[]>([]);
  const [all, setAll] = React.useState<RawTxn[]>([]);
  const [matSelectOpen, setMatSelectOpen] = React.useState(false);
  const [openingBalance, setOpeningBalance] = React.useState<string>('0');
  const [perWarehouse, setPerWarehouse] = React.useState(false);

  // Warehouses map id -> name
  const { data: whDataRaw = [] } = useGetMatClntWarehouses() as any;
  const warehouses: any[] = Array.isArray(whDataRaw) ? whDataRaw : (whDataRaw?.data ?? []);
  const whMap = React.useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach((w: any) => m.set(Number(w.id), String(w.name)));
    return m;
  }, [warehouses]);

  // Material descriptions via inventory
  const { data: invRaw = [] } = useGetMatClntWhInventory() as any;
  const invList: any[] = Array.isArray(invRaw) ? invRaw : (invRaw?.data ?? []);
  const itemDescMap = React.useMemo(() => {
    const m = new Map<string, string>();
    (invList || []).forEach((row: any) => {
      const code = String(row.itemcode || '').trim();
      if (!code) return;
      if (!m.has(code)) m.set(code, String(row.description || ''));
    });
    return m;
  }, [invList]);

  const escape = (v: string) => v.replace(/'/g, "''");

  const buildWhere = React.useCallback(() => {
    const parts: string[] = [];
    if (filters.itemcode) parts.push(`itemcode = '${escape(String(filters.itemcode))}'`);
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      parts.push(`txn_date >= '${escape(filters.dateRange[0])}' AND txn_date <= '${escape(filters.dateRange[1])}'`);
    }
    if (filters.warehouseId != null) {
      const wh = Number(filters.warehouseId);
      parts.push(`(to_warehouse_id = ${wh} OR from_warehouse_id = ${wh})`);
    }
    return parts.length ? `WHERE ${parts.join(' AND ')}` : '';
  }, [filters.itemcode, filters.dateRange, filters.warehouseId]);

  const runQuery = React.useCallback(async () => {
    try {
      setLoading(true);
      const where = buildWhere();
      const sql = `
        SELECT id, txn_date, op_type, reference_no,
               from_warehouse_id, to_warehouse_id,
               itemcode, qty, unit, issued_to, created_by, notes
        FROM mat_clnt_txns
        ${where}
        ORDER BY txn_date ASC, id ASC
      `;
      const data = await fetchSqlDataWithQuery(sql);
      const arr: RawTxn[] = Array.isArray(data) ? (data as any) : (data?.rows || data?.data || []);
      setAll(arr || []);

      // Opening balance query up to (but excluding) start date
      let opening = new Decimal(0);
      const start = filters.dateRange?.[0];
      if (filters.itemcode && start) {
        const code = escape(String(filters.itemcode));
        const wh = filters.warehouseId;
        let openSql = '';
        if (wh != null) {
          openSql = `SELECT COALESCE(SUM(CASE WHEN to_warehouse_id = ${Number(wh)} THEN qty ELSE 0 END)
                             - SUM(CASE WHEN from_warehouse_id = ${Number(wh)} THEN qty ELSE 0 END), 0) AS opening
                     FROM mat_clnt_txns
                     WHERE itemcode = '${code}' AND txn_date < '${escape(start)}'`;
        } else {
          openSql = `SELECT COALESCE(SUM(CASE WHEN to_warehouse_id IS NOT NULL THEN qty ELSE 0 END)
                             - SUM(CASE WHEN from_warehouse_id IS NOT NULL THEN qty ELSE 0 END), 0) AS opening
                     FROM mat_clnt_txns
                     WHERE itemcode = '${code}' AND txn_date < '${escape(start)}'`;
        }
        const odata = await fetchSqlDataWithQuery(openSql);
        const oval = Array.isArray(odata) ? (odata[0]?.opening ?? 0) : (odata?.rows?.[0]?.opening ?? odata?.data?.[0]?.opening ?? 0);
        opening = toDec(oval);
      }
      setOpeningBalance(opening.toFixed(DEC_PLACES));
    } catch (e) {
      message.error('Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  }, [buildWhere, filters.dateRange, filters.itemcode, filters.warehouseId]);

  React.useEffect(() => { runQuery(); }, [runQuery]);

  // Derive ledger view with in/out and running balance + totals
  React.useEffect(() => {
    const wh = filters.warehouseId;
    const enriched = (all || []).map<LedgerRow>((r) => {
      const useWh = perWarehouse && wh != null;
      const qty = toDec(r.qty);
      const in_qty = useWh
        ? (Number(r.to_warehouse_id) === Number(wh) ? qty : new Decimal(0))
        : (r.to_warehouse_id ? qty : new Decimal(0));
      const out_qty = useWh
        ? (Number(r.from_warehouse_id) === Number(wh) ? qty : new Decimal(0))
        : (r.from_warehouse_id ? qty : new Decimal(0));
      return { ...r, in_qty: in_qty.toFixed(DEC_PLACES), out_qty: out_qty.toFixed(DEC_PLACES), balance: '0' };
    });
    let running = toDec(openingBalance);
    const withBal = enriched.map((r) => {
      running = running.add(toDec(r.in_qty)).sub(toDec(r.out_qty));
      return { ...r, balance: running.toFixed(DEC_PLACES) };
    });
    const sumIn = withBal.reduce((s, r) => s.add(toDec(r.in_qty)), new Decimal(0));
    const sumOut = withBal.reduce((s, r) => s.add(toDec(r.out_qty)), new Decimal(0));
    const net = sumIn.sub(sumOut);
    const totals: LedgerRow = {
      id: -1,
      txn_date: withBal[withBal.length - 1]?.txn_date || (filters.dateRange?.[1] || ''),
      op_type: 'TOTAL' as any,
      reference_no: 'Totals',
      from_warehouse_id: null as any,
      to_warehouse_id: null as any,
      itemcode: String(filters.itemcode || ''),
      qty: net.toFixed(DEC_PLACES) as any,
      unit: '',
      issued_to: null as any,
      created_by: null as any,
      notes: null as any,
      in_qty: sumIn.toFixed(DEC_PLACES),
      out_qty: sumOut.toFixed(DEC_PLACES),
      balance: toDec(openingBalance).add(net).toFixed(DEC_PLACES),
    };
    setRows(withBal.concat(withBal.length ? [totals] : []));
  }, [all, filters.warehouseId, perWarehouse, openingBalance, filters.itemcode, filters.dateRange]);

  const columns: ColumnsType<LedgerRow> = React.useMemo(() => [
    { title: 'Date', dataIndex: 'txn_date', key: 'txn_date', width: 130, sorter: (a, b) => dayjs(a.txn_date).valueOf() - dayjs(b.txn_date).valueOf(), render: (v: string) => dayjs(v).format('YYYY-MM-DD') },
    { title: 'Ref', dataIndex: 'reference_no', key: 'reference_no', width: 160 },
    { title: 'Type', dataIndex: 'op_type', key: 'op_type', width: 120 },
    { title: 'Item', key: 'item', width: 300, render: (_, r) => `${r.itemcode} - ${itemDescMap.get(String(r.itemcode)) || ''}` },
    { title: 'From WH', dataIndex: 'from_warehouse_id', key: 'from_warehouse_id', width: 160, render: (v: number) => whMap.get(Number(v)) || v },
    { title: 'Out', dataIndex: 'out_qty', key: 'out_qty', width: 110, align: 'right', render: (v) => formatNumberSmart(v) },
    { title: 'To WH', dataIndex: 'to_warehouse_id', key: 'to_warehouse_id', width: 160, render: (v: number) => whMap.get(Number(v)) || v },
    { title: 'In', dataIndex: 'in_qty', key: 'in_qty', width: 110, align: 'right', render: (v) => formatNumberSmart(v) },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 110, align: 'right', render: (v) => formatNumberSmart(v) },
    { title: 'Balance', dataIndex: 'balance', key: 'balance', width: 130, align: 'right', render: (v) => formatNumberSmart(v) },
    { title: 'Issued To', dataIndex: 'issued_to', key: 'issued_to', width: 180 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true },
  ], [itemDescMap, whMap]);

  return (
    <Card title="Client Material Ledger" bodyStyle={{ padding: 8 }}>
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker
          value={
            filters.dateRange
              ? [dayjs(filters.dateRange[0] || undefined), dayjs(filters.dateRange[1] || undefined)]
              : null
          }
          onChange={(vals) => {
            if (!vals || !vals[0] || !vals[1]) { setFilters((p) => ({ ...p, dateRange: null })); return; }
            setFilters((p) => ({ ...p, dateRange: [vals[0]!.format('YYYY-MM-DD'), vals[1]!.format('YYYY-MM-DD')] }));
          }}
        />
        <Space.Compact>
          <Input
            placeholder="Item Code"
            value={filters.itemcode ?? ''}
            onChange={(e) => setFilters((p) => ({ ...p, itemcode: (e.target.value || '').trim() || null }))}
            style={{ width: 200 }}
            allowClear
          />
          <Button icon={<SearchOutlined />} onClick={() => setMatSelectOpen(true)} aria-label="Select material" />
        </Space.Compact>
        <Select
          placeholder="Warehouse"
          allowClear
          value={filters.warehouseId ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, warehouseId: (v as number) ?? null }))}
          options={(warehouses || []).map((w: any) => ({ value: Number(w.id), label: String(w.name) }))}
          style={{ width: 220 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <span>
          <Typography.Text strong>Per Warehouse</Typography.Text>{' '}
          <Switch checked={perWarehouse} onChange={setPerWarehouse} />
        </span>
        <Button type="primary" onClick={runQuery}>Refresh</Button>
        <Button onClick={() => { setFilters({ dateRange: null, itemcode: null, warehouseId: null }); setPerWarehouse(false); }}>Reset</Button>
      </Space>

      <StandardTable<LedgerRow>
        columns={columns}
        dataSource={rows}
        rowKey={(r) => String(r.id)}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        enableExport
      />

      <MatClntMasterMaterialSelect
        open={matSelectOpen}
        onCancel={() => setMatSelectOpen(false)}
        onOk={(selected) => {
          const first = Array.isArray(selected) && selected.length > 0 ? selected[0] : undefined;
          if (first && first.itemcode) {
            setFilters((p) => ({ ...p, itemcode: String(first.itemcode) }));
          }
          setMatSelectOpen(false);
        }}
        title="Select Client Material"
      />
    </Card>
  );
}
